// Hero rigs: glTF skins with role clips, or a procedural stand-in until a model exists. Ported from
// prototypes/match/js/heroes.js, plus driveHero() — the per-hero body of sim.js's drive(), which is the
// view's job now that core carries no rig/ring. Anim events (core/events.ts) land here.
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { mk, adoptMesh, release, TEAM } from './engrave.js';
import type { Team } from '../core/layout.js';
import type { HeroDef } from '../core/heroes.js';
import type { AnimEvent } from '../core/events.js';
import type { GameView, ViewHero } from './gameView.js';
import { strideSpeed, trackPace, runRate, type Pace } from './gait.js';

export const LOOPING = new Set(['Idle', 'Run', 'Charge', 'Brace', 'AimCharge', 'Lunge', 'Flame']);

export type Rig = {
  root: THREE.Group;
  body: THREE.Group | null;
  arm: THREE.Group | null;
  float?: boolean;
  mixer: THREE.AnimationMixer | null;
  actions: Record<string, THREE.AnimationAction> | null;
  current: THREE.AnimationAction | null;
  base: string | null;
  /** Ground speed the Run clip's planted feet imply at this model's scale, m/s (gait.ts); null: no feet. */
  stride?: number | null;
  /** The hero's measured ground speed, which paces the Run clip (driveHero). */
  pace?: Pace;
  /** A cached glTF is mounted: its geometry is shared with every other clone of that model. */
  model?: boolean;
  /** Set on match stop: a model still loading then must not register its meshes with the engraver. */
  disposed?: boolean;
};

export function playClip(rig: Rig, name: string, fade = 0.12): boolean {
  const next = rig.actions && rig.actions[name];
  if (!next) return false;
  if (LOOPING.has(name)) rig.base = name;
  const prev = rig.current;
  if (prev === next && LOOPING.has(name)) return true;
  next.reset().setEffectiveWeight(1).fadeIn(fade).play();
  if (prev && prev !== next) prev.fadeOut(fade);
  rig.current = next;
  return true;
}
export function isBusy(rig: Rig): boolean {
  return !!(rig.current && !LOOPING.has(rig.current.getClip().name) && rig.current.isRunning());
}
/* Switch the looping base clip unless a one-shot is still playing on top of it. */
export function setBase(rig: Rig, name: string) {
  if (!rig.actions || rig.base === name) return;
  rig.base = name;
  if (!isBusy(rig)) playClip(rig, name, 0.2);
}

/* Dedicated character clip first, class clip as fallback — skills.js's old play() helper, now view-side. */
export function play(rig: Rig, clips: string | string[], fade = 0.08): string | null {
  for (const c of ([] as string[]).concat(clips)) if (playClip(rig, c, fade)) return c;
  return null;
}
const hasClip = (rig: Rig, name: string) => !!(rig.actions && rig.actions[name]);

/* One AnimEvent in, at most one playClip out. Pure enough to unit-test with a fake `actions` map. */
export function applyAnimEvent(rig: Rig, e: AnimEvent) {
  if (e.unless && e.unless.some(c => hasClip(rig, c))) return;
  if (e.skipIfBusy && isBusy(rig)) return;
  // matches skills.js's recover(): arms the eventual fallback first, without cutting a running one-shot
  if (e.resetBase) rig.base = 'Idle';
  play(rig, e.clips, e.fade);
}

/* ---- procedural stand-ins (heroes without a Blender model yet) ---- */
const G = {
  box: (w: number, h: number, d: number) => new THREE.BoxGeometry(w, h, d),
  cyl: (rt: number, rb: number, h: number, s = 12) => new THREE.CylinderGeometry(rt, rb, h, s),
  sph: (r: number) => new THREE.SphereGeometry(r, 16, 12),
  cone: (r: number, h: number, s = 10) => new THREE.ConeGeometry(r, h, s),
  cap: (r: number, l: number) => new THREE.CapsuleGeometry(r, l, 4, 10),
  tor: (r: number, t: number, arc = Math.PI * 2) => new THREE.TorusGeometry(r, t, 6, 24, arc),
};
const HALF = Math.PI / 2, SKIN = 0xe0c09a;
type Builder = (ctx: { add: AddFn; body: THREE.Group; arm: THREE.Group; rig: { float: boolean } }) => void;
type AddFn = (parent: THREE.Group, geo: THREE.BufferGeometry, color: number | 'T', pos?: [number, number, number], rot?: [number, number, number] | 0, scale?: [number, number, number] | 0, opts?: Record<string, any>) => THREE.Mesh;
const BUILDERS: Record<string, Builder> = {
  alchemist: ({ add, body, arm }) => {
    const robe = 0x4d3348;
    add(body, G.cone(.62, 1.75), robe, [0, .88, 0]);
    add(body, G.sph(.36), robe, [0, 1.62, 0], 0, [1.2, .9, 1]);
    add(body, G.tor(.22, .05), 'T', [0, 1.2, 0], [HALF, 0, 0]);
    add(body, G.sph(.26), SKIN, [0, 1.98, .04]);
    add(body, G.cone(.2, .55, 8), 0xd6d0c4, [0, 1.66, .2], [Math.PI, 0, 0]);
    for (const s of [-1, 1]) add(body, G.tor(.06, .015), 0xc9a44a, [.1 * s, 2.0, .26]);
    add(body, G.sph(.32), 'T', [0, 2.2, -.02], [-.2, 0, 0], [1, .35, 1]);
    add(body, G.sph(.3), 0x8fb8b0, [0, 1.55, -.45]);
    add(body, G.cyl(.04, .04, .7), 0xb87333, [.2, 1.9, -.35], [.5, 0, -.4]);
    arm.position.set(.4, 1.55, .05);
    add(arm, G.cap(.1, .4), robe, [0, -.2, .15], [1.1, 0, 0]);
    add(arm, G.sph(.15), 0x9fe07a, [0, -.3, .5], 0, 0, { fx: true });
  },
  reaper: ({ add, body, arm, rig }) => {
    rig.float = true;
    const cloth = 0x1f1e23;
    add(body, G.cone(.58, 1.85, 9), cloth, [0, 1.0, 0]);
    add(body, G.sph(.38), cloth, [0, 1.7, -.02], 0, [1.15, .9, 1]);
    add(body, G.sph(.36), cloth, [0, 2.05, -.05], 0, [1, 1.15, 1.1]);
    add(body, G.sph(.22), 0xe6e0d0, [0, 1.98, .14], 0, [.9, 1, .9]);
    for (const s of [-1, 1]) add(body, G.sph(.05), 0x0e0d10, [.08 * s, 2.02, .33]);
    add(body, G.tor(.22, .06), 'T', [0, 1.3, 0], [HALF, 0, 0]);
    add(body, G.cone(.07, .12, 6), 0xc9a44a, [.2, 1.12, .12]);
    add(body, G.cone(.07, .12, 6), 0xc9a44a, [.2, 1.24, .12], [Math.PI, 0, 0]);
    arm.position.set(.42, 1.6, .05);
    add(arm, G.cyl(.035, .035, 2.5), 0x4a3a2a, [0, -.1, .35], [0, 0, .1]);
    add(arm, G.tor(.55, .05, Math.PI * .6), 0xcfd2d6, [-.2, 1.1, .35], [HALF, 0, Math.PI * .9]);
  },
};

function standIn(kind: string, team: Team) {
  const root = new THREE.Group(), body = new THREE.Group(), arm = new THREE.Group();
  root.add(body); body.add(arm);
  const rig = { root, body, arm, float: false };
  const add: AddFn = (parent, geo, color, pos = [0, 0, 0], rot = 0, scale = 0, opts = {}) => {
    const accent = color === 'T';
    const m = mk(geo, accent ? TEAM[team] : color, { ...opts, team: accent ? team : 'unit' });
    m.position.set(...pos);
    if (rot) m.rotation.set(...rot);
    if (scale) m.scale.set(...scale);
    parent.add(m);
    return m as unknown as THREE.Mesh;
  };
  (BUILDERS[kind] as Builder)({ add, body, arm, rig });
  return rig;
}

/* Procedural motion for stand-ins: bob, breathe, swing the weapon arm. */
export function animateStandIn(rig: Rig, t: number, moving: boolean, swing: number) {
  if (rig.mixer || !rig.body) return;
  const r = rig as unknown as { body: THREE.Group; arm: THREE.Group; float?: boolean };
  r.body.position.y = r.float ? 0.35 + Math.sin(t * 2) * 0.1 : (moving ? Math.abs(Math.sin(t * 8)) * 0.06 : 0);
  r.body.scale.y = 1 + Math.sin(t * 2.2) * 0.015;
  r.arm.rotation.x = -swing * 1.1 + Math.sin(t * 1.6) * 0.06;
}

const loader = new GLTFLoader();

/* Our models ship as glTF JSON with the buffer inlined as base64 (the artifact host serves .json, not .glb).
   The host's CSP blocks fetching data: URIs, so decode the buffer here and hand GLTFLoader a GLB in memory.
   Kept even though this build is self-contained: it is still how the source .gltf.json assets are packaged. */
async function fetchModelGlb(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const doc = await res.json();
  let bin = new Uint8Array(0);
  const buf = doc.buffers && doc.buffers[0];
  if (buf && buf.uri && buf.uri.startsWith('data:')) {
    const raw = atob(buf.uri.slice(buf.uri.indexOf(',') + 1));
    bin = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) bin[i] = raw.charCodeAt(i);
    delete buf.uri;
  }
  const json = new TextEncoder().encode(JSON.stringify(doc));
  const pad = (n: number) => (4 - (n % 4)) % 4;
  const jsonLen = json.length + pad(json.length), binLen = bin.length + pad(bin.length);
  const total = 12 + 8 + jsonLen + (binLen ? 8 + binLen : 0);
  const out = new ArrayBuffer(total), dv = new DataView(out), u8 = new Uint8Array(out);
  dv.setUint32(0, 0x46546C67, true); dv.setUint32(4, 2, true); dv.setUint32(8, total, true);
  dv.setUint32(12, jsonLen, true); dv.setUint32(16, 0x4E4F534A, true);
  u8.set(json, 20); u8.fill(0x20, 20 + json.length, 20 + jsonLen);
  if (binLen) {
    const o = 20 + jsonLen;
    dv.setUint32(o, binLen, true); dv.setUint32(o + 4, 0x004E4942, true);
    u8.set(bin, o + 8);
  }
  return out;
}
/* Parsed once per URL for the page's lifetime ("play again" never reloads); every caller gets its own
   clone — own bones and skeleton, shared geometry, shared source materials that adoptMesh() only reads.
   Clips are shared too: mixers only read them, and monsterView deep-copies the one it rewrites. The
   request itself is cached, so concurrent callers share one fetch+parse; a failure drops it for a retry. */
type ParsedModel = { scene: THREE.Group; animations: THREE.AnimationClip[] };
const parsedModels = new Map<string, Promise<ParsedModel>>();
const instance = (m: ParsedModel) => ({ scene: cloneModel(m.scene), animations: m.animations });

/* Like SkeletonUtils.clone, except meshes that shared one skeleton in the source share one in the clone:
   a skeleton per mesh would multiply bone-matrix updates and bone textures by the mesh count. */
function cloneModel(src: THREE.Group): THREE.Group {
  const copy = src.clone(), twin = new Map<THREE.Object3D, THREE.Object3D>();
  const pair = (a: THREE.Object3D, b: THREE.Object3D) => { twin.set(a, b); a.children.forEach((c, i) => pair(c, b.children[i]!)); };
  pair(src, copy);
  const skeletons = new Map<THREE.Skeleton, THREE.Skeleton>();
  copy.traverse(o => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isSkinnedMesh) return;
    let sk = skeletons.get(m.skeleton);
    if (!sk) { sk = new THREE.Skeleton(m.skeleton.bones.map(b => twin.get(b) as THREE.Bone), m.skeleton.boneInverses); skeletons.set(m.skeleton, sk); }
    m.bind(sk, m.bindMatrix);
  });
  return copy;
}
export function loadModel(gltfLoader: GLTFLoader, url: string, onLoad: (g: any) => void, onError: (e: any) => void) {
  let parsed = parsedModels.get(url);
  if (!parsed) {
    parsed = fetchModelGlb(url).then(glb => new Promise<ParsedModel>((resolve, reject) =>
      gltfLoader.parse(glb, '', (gltf: any) => resolve({ scene: gltf.scene, animations: gltf.animations }), reject)));
    parsedModels.set(url, parsed);
    parsed.catch(() => { if (parsedModels.get(url) === parsed) parsedModels.delete(url); });
  }
  parsed.then(m => onLoad(instance(m))).catch(onError);
}

/** Test seam: a stubbed fetch that never settles would otherwise pin its URL's entry for the module's life. */
export function clearModelCache() { parsedModels.clear(); }

declare global {
  interface Window { __diagFn?: (msg: string) => void }
}

/* A rig object whose content can be swapped (stand-in → model) without the game noticing. */
export function createRig(scene: THREE.Scene, def: HeroDef, team: Team): Rig {
  const holder: Rig = { root: new THREE.Group(), body: null, arm: null, mixer: null, actions: null, current: null, base: null };
  scene.add(holder.root);
  // What mount() put under the root. Only that is swapped and freed: others hang things there too (the wolf
  // of wolfFormView.ts), possibly before any content exists — a hero without a stand-in waits for its model.
  let content: THREE.Object3D | null = null;
  const mount = (inner: { root: THREE.Group; body: THREE.Group | null; arm: THREE.Group | null; float?: boolean; mixer?: THREE.AnimationMixer | null; actions?: Record<string, THREE.AnimationAction> | null; stride?: number | null }) => {
    if (content) holder.root.remove(content);
    holder.root.add(inner.root);
    // content stays first: fx.ts reads root.children[0] as the rig's content to notice a model mounting
    const kids = holder.root.children;
    kids.splice(kids.indexOf(inner.root), 1);
    kids.unshift(inner.root);
    content = inner.root;
    Object.assign(holder, { body: inner.body, arm: inner.arm, float: inner.float, mixer: inner.mixer || null,
                            actions: inner.actions || null, current: null, base: null, stride: inner.stride ?? null });
  };
  if (def.builder) mount(standIn(def.builder, team));
  const load = (url: string, fallback?: string) => {
    loadModel(loader, url, (gltf) => { try {
      if (holder.disposed) return;
      const model = gltf.scene as THREE.Group;
      model.scale.setScalar(def.scale || 1);
      const runClip = (gltf.animations as THREE.AnimationClip[]).find(c => c.name === 'Run');
      const stride = runClip ? strideSpeed(model, runClip) : null;  // before the rig's own mixer poses it
      model.traverse((o: THREE.Object3D) => { if ((o as THREE.Mesh).isMesh) adoptMesh(o as THREE.Mesh, team); });
      const inner = { root: new THREE.Group(), body: new THREE.Group(), arm: new THREE.Group(), stride: stride && stride * model.scale.x } as { root: THREE.Group; body: THREE.Group; arm: THREE.Group; mixer?: THREE.AnimationMixer; actions?: Record<string, THREE.AnimationAction>; stride: number | null };
      inner.root.add(inner.body);
      inner.body.add(model);
      inner.mixer = new THREE.AnimationMixer(model);
      inner.actions = {};
      for (const clip of gltf.animations as THREE.AnimationClip[]) {
        const a = inner.mixer.clipAction(clip);
        if (!LOOPING.has(clip.name)) { a.setLoop(THREE.LoopOnce, 1); a.clampWhenFinished = true; }
        inner.actions[clip.name] = a;
      }
      if (content) release(content);  // the stand-in
      mount(inner);
      holder.model = true;
      (holder.mixer as THREE.AnimationMixer).addEventListener('finished', (e: any) => {
        if (e.action === holder.current && e.action.getClip().name !== 'Death' && holder.base) playClip(holder, holder.base, 0.2);
      });
      playClip(holder, 'Idle', 0);
    } catch (e: any) { if (window.__diagFn) window.__diagFn('mount ' + url + ': ' + e.message); throw e; } }, (err: any) => {
      if (window.__diagFn) window.__diagFn('model ' + url + ': ' + (err && err.message || err));
      if (fallback) load(fallback);  // model not built yet: borrow the class's first model
      else console.warn('Model failed to load, keeping the stand-in:', url);
    });
  };
  if (def.model) load(def.model, def.fallback);
  return holder;
}

/* Match stop: off the scene, out of the engraver, GPU side freed (a cached model's geometry excepted). */
export function disposeRig(scene: THREE.Scene, rig: Rig) {
  rig.disposed = true;
  scene.remove(rig.root);
  rig.mixer?.stopAllAction();
  release(rig.root, { geometry: !rig.model });
}

export type HeroView = { hero: ViewHero; rig: Rig };

/* The per-hero body of sim.js's drive(): position/orient the rig, run the mixer, pick the
   procedural stand-in pose. Turn-rate easing (h.yaw) is NOT here — core now owns it (game.ts's
   updateYaw), since it is simulation state, not a mesh; this only ever reads the result. `view` decides
   "is this hero moving" (GameView.movingOf) rather than this function inlining core's own path/pending
   fields — the one piece here that isn't just a raw Hero field, so it stays a method both a local Game
   and a server snapshot stream can each answer their own way, instead of ViewHero growing a field the
   real core Hero doesn't have. */
export function driveHero(view: GameView, hv: HeroView, t: number, dt: number) {
  const h = hv.hero, rig = hv.rig;
  const moving = view.movingOf(h);
  rig.root.position.set(h.pos.x, h.lift, h.pos.z);
  rig.root.rotation.y = h.yaw;
  if (h.alive && h.state === 'normal' && t >= h.stunUntil) setBase(rig, moving ? 'Run' : 'Idle');
  const speed = trackPace(rig.pace ??= { x: h.pos.x, z: h.pos.z, speed: h.def.speed }, h.pos, moving, dt);
  const run = rig.actions?.Run;
  if (run && rig.stride) run.timeScale = runRate(speed, rig.stride);  // feet keep pace with the ground
  if (rig.mixer) rig.mixer.update(t < h.stunUntil ? dt * 0.15 : dt);  // stunned heroes barely move
  const swing = Math.max(0, 1 - (t - h.swingAt) / 0.3);
  animateStandIn(rig, t, moving, Math.sin(swing * Math.PI));
  if (!rig.mixer && rig.body) rig.body.rotation.z = h.alive ? 0 : Math.PI / 2;
}
